import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface VideoCard {
  id: number;
  name: string;
  location?: string;
  description: string;
  youtubeId: string | null;
  thumbnail: string;
  isShort?: boolean;
}

const testimonials: VideoCard[] = [
  {
    id: 1,
    name: 'Elisa',
    location: 'Japão · 20 anos no exterior',
    description: '"Morar longe por tanto tempo cria uma tensão silenciosa que a gente nem percebe mais. Foi a primeira vez que me senti acolhida de verdade."',
    youtubeId: 'U60zRK9ZZh0',
    thumbnail: 'https://public.readdy.ai/ai/img_res/7eb040420773528fe68c173fa6c08a69.jpg',
    isShort: true,
  },
  {
    id: 2,
    name: 'Lucas',
    location: 'Empresário · Ramo alimentício',
    description: '"A pressão do dia a dia na empresa virou um acúmulo que eu não sabia mais carregar. Essa sessão me deu uma clareza que eu não esperava encontrar tão rápido."',
    youtubeId: '4ZfFFe1su9I',
    thumbnail: 'https://readdy.ai/api/search-image?query=confident%20calm%20brazilian%20businessman%20sitting%20in%20modern%20minimalist%20office%20with%20warm%20lighting%20relaxed%20posture%20open%20collar%20shirt%20subtle%20smile%20natural%20light%20through%20window%20plants%20soft%20beige%20neutral%20tones%20grounded%20professional%20atmosphere&width=360&height=640&seq=lucasempresario&orientation=portrait',
    isShort: true,
  },
  {
    id: 3,
    name: 'Márcia Ferrari',
    location: 'Dona de casa',
    description: '"Eu achava que esse tipo de coisa não era pra mim. Mas foi exatamente o que eu precisava. Saí mais leve e com vontade de cuidar de mim mesma."',
    youtubeId: 'A4feGcAG1eE',
    thumbnail: 'https://readdy.ai/api/search-image?query=warm%20brazilian%20woman%20in%20cozy%20home%20environment%20soft%20natural%20afternoon%20light%20gentle%20relaxed%20expression%20sitting%20near%20window%20with%20indoor%20plants%20cup%20of%20tea%20calm%20domestic%20atmosphere%20emotional%20wellness%20soft%20warm%20beige%20tones%20peaceful%20and%20grounded&width=360&height=640&seq=marciaferrari&orientation=portrait',
    isShort: true,
  },
];

function VideoCard({ card, index, visible }: { card: VideoCard; index: number; visible: boolean }) {
  const [playing, setPlaying] = useState(false);

  const handlePlay = () => {
    if (card.youtubeId) setPlaying(true);
  };

  const aspectStyle = card.isShort
    ? { paddingBottom: '177.78%' } // 9:16 for Shorts
    : { paddingBottom: '56.25%' }; // 16:9 for normal

  return (
    <div
      className="flex flex-col rounded-2xl overflow-hidden border"
      style={{
        background: '#0F1E30',
        borderColor: card.youtubeId ? 'rgba(0,200,232,0.22)' : 'rgba(0,200,232,0.15)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.6s ease ${index * 0.15}s, transform 0.6s ease ${index * 0.15}s`,
      }}
    >
      {/* Video Area */}
      <div className="relative w-full" style={aspectStyle}>
        {playing && card.youtubeId ? (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${card.youtubeId}?autoplay=1&rel=0`}
            title={card.name}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <>
            {/* Thumbnail */}
            <img
              src={card.thumbnail}
              alt={card.name}
              className="absolute inset-0 w-full h-full"
              style={{ objectFit: 'cover', objectPosition: 'center' }}
            />

            {/* Dark overlay */}
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to bottom, rgba(6,13,24,0.2) 0%, rgba(6,13,24,0.6) 100%)' }}
            />

            {/* Play Button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <button
                onClick={handlePlay}
                className="group flex items-center justify-center rounded-full border-2 transition-all duration-300 cursor-pointer"
                style={{
                  width: 64,
                  height: 64,
                  background: card.youtubeId ? 'rgba(0,200,232,0.18)' : 'rgba(245,165,32,0.12)',
                  borderColor: card.youtubeId ? '#00C8E8' : '#F5A520',
                }}
                title={card.youtubeId ? 'Assistir depoimento' : 'Vídeo em breve'}
              >
                {card.youtubeId ? (
                  <i
                    className="ri-play-fill text-2xl"
                    style={{ color: '#00C8E8', marginLeft: 3 }}
                  />
                ) : (
                  <i
                    className="ri-time-line text-xl"
                    style={{ color: '#F5A520' }}
                  />
                )}
              </button>
            </div>

            {/* Short badge */}
            {card.isShort && (
              <div
                className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold tracking-wide flex items-center gap-1"
                style={{ background: 'rgba(0,200,232,0.15)', color: '#00C8E8', border: '1px solid rgba(0,200,232,0.3)' }}
              >
                <i className="ri-youtube-line text-sm" />
                Short
              </div>
            )}

            {/* Duration badge */}
            {!card.isShort && (
              <div
                className="absolute bottom-3 right-3 px-2 py-0.5 rounded text-xs font-semibold"
                style={{ background: 'rgba(6,13,24,0.75)', color: '#B8D4E8' }}
              >
                ~2 min
              </div>
            )}

            {/* Coming soon badge */}
            {!card.youtubeId && (
              <div
                className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold tracking-wide"
                style={{ background: 'rgba(245,165,32,0.15)', color: '#F5A520', border: '1px solid rgba(245,165,32,0.3)' }}
              >
                Em breve
              </div>
            )}
          </>
        )}
      </div>

      {/* Card Info */}
      <div className="px-5 py-4">
        {card.location && (
          <p className="text-xs font-semibold tracking-wide mb-1" style={{ color: '#00C8E8' }}>
            {card.location}
          </p>
        )}
        <p
          className="text-sm italic leading-relaxed"
          style={{ color: '#8AAFC8' }}
        >
          {card.description}
        </p>
      </div>
    </div>
  );
}

export default function VideoTestimonials() {
  const { ref, visible } = useScrollAnimation();

  return (
    <section className="py-24 px-6" style={{ background: '#060D18' }}>
      <div ref={ref} className="max-w-5xl mx-auto">
        {/* Header */}
        <div
          className="text-center mb-14"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <span
            className="inline-block text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-6 border"
            style={{ background: 'rgba(245,165,32,0.08)', color: '#F5A520', borderColor: 'rgba(245,165,32,0.2)' }}
          >
            Depoimentos reais
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
            Quem já viveu essa experiência
          </h2>
          <p className="text-base max-w-xl mx-auto" style={{ color: '#5A7A95' }}>
            Pessoas como você, que também duvidavam — e decidiram experimentar.
          </p>
        </div>

        {/* Cards — first card aligns top since it's taller (Short) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {testimonials.map((card, i) => (
            <VideoCard key={card.id} card={card} index={i} visible={visible} />
          ))}
        </div>

        {/* Bottom note */}
        <div
          className="mt-10 text-center"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.6s ease 0.5s',
          }}
        >
          <p className="text-xs" style={{ color: '#3A5A70' }}>
            Depoimentos voluntários. Resultados podem variar de pessoa para pessoa.
          </p>
        </div>
      </div>
    </section>
  );
}
